import React from 'react';
import {
  Box,
  Pagination,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';

interface PaginatedListProps {
  children: React.ReactNode;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
  error?: string;
  emptyMessage?: string;
  showPagination?: boolean;
}

const PaginatedList: React.FC<PaginatedListProps> = ({
  children,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  loading = false,
  error,
  emptyMessage = 'Nu există elemente de afișat',
  showPagination = true
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (totalItems === 0) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        py={6}
        textAlign="center"
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {emptyMessage}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Încearcă să modifici filtrele sau să adaugi conținut nou
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Info despre paginare */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          Afișând {startItem}-{endItem} din {totalItems} elemente
        </Typography>
        
        {totalPages > 1 && (
          <Typography variant="body2" color="text.secondary">
            Pagina {currentPage} din {totalPages}
          </Typography>
        )}
      </Box>

      {/* Conținutul listei */}
      {children}

      {/* Paginare */}
      {showPagination && totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => onPageChange(page)}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>
      )}
    </Box>
  );
};

export default PaginatedList;
